import mapboxgl from 'mapbox-gl';
import { Team } from '../types';
import { TerritoryGeoJSON } from '../services/territoryService';
import { featureCentroid } from './geo';
import { getSiteOrigin, isDiscordEmbedded, resolveSiteAssetUrl } from '../discord/discordActivity';

function logoFolder(leagueId: string): string | null {
    return leagueId.startsWith('ncaa') ? 'ncaa' : leagueId;
}

function isSiteLogoPath(path: string): boolean {
    if (path.startsWith('/logos/')) return true;
    try {
        const url = new URL(path);
        const siteHost = new URL(getSiteOrigin()).host;
        return url.host === siteHost && url.pathname.startsWith('/logos/');
    } catch {
        return false;
    }
}

/**
 * One known path per team — no png→jpg→jpeg→svg waterfall.
 * Prefer local `/logos/{folder}/{espnId}.png`, then DB logo_url, then ESPN CDN.
 */
export function buildTeamLogoPaths(team: Team, leagueId: string): string[] {
    const paths: string[] = [];
    const seen = new Set<string>();
    const add = (path: string) => {
        const resolved = resolveSiteAssetUrl(path);
        if (!seen.has(resolved)) {
            seen.add(resolved);
            paths.push(resolved);
        }
    };

    const absTeamId = team.espn_team_id ? Math.abs(Number(team.espn_team_id)) : null;
    const folder = logoFolder(leagueId);

    // Site-hosted logos are committed as `{espn_id}.png` under the league folder.
    if (absTeamId && folder) {
        add(`/logos/${folder}/${absTeamId}.png`);
    }

    if (team.logo_url) {
        const url = team.logo_url;
        if (url.startsWith('/') || isSiteLogoPath(url)) {
            add(url.startsWith('/') ? url : new URL(url).pathname);
        } else if (!isDiscordEmbedded()) {
            paths.push(url);
        }
    }

    // Final fallback when local file is missing and DB has no usable URL.
    if (absTeamId && folder === 'ncaa' && paths.length <= 1) {
        add(`https://a.espncdn.com/i/teamlogos/ncaa/500/${absTeamId}.png`);
    }

    return paths;
}

export interface TeamMarkerAnchor {
    teamId: number;
    lat: number;
    lng: number;
    countyCount: number;
}

/** One marker per team (or per team+state when splitByState) at area-weighted centroids. */
export function computeTeamMarkerAnchors(
    geojson: TerritoryGeoJSON,
    options?: { splitByState?: boolean },
): TeamMarkerAnchor[] {
    const byKey = new Map<string, { teamId: number; lat: number; lng: number; area: number; count: number }>();

    geojson.features.forEach((feature) => {
        const teamId = Number(feature.properties.team_id);
        if (Number.isNaN(teamId)) return;
        const state = feature.properties.state_name || 'unknown';
        const key = options?.splitByState ? `${teamId}:${state}` : String(teamId);
        const c = featureCentroid(feature.geometry);
        if (!c) return;

        const prev = byKey.get(key) || { teamId, lat: 0, lng: 0, area: 0, count: 0 };
        byKey.set(key, {
            teamId,
            lat: prev.lat + c.lat * c.area,
            lng: prev.lng + c.lng * c.area,
            area: prev.area + c.area,
            count: prev.count + 1,
        });
    });

    const anchors: TeamMarkerAnchor[] = [];
    byKey.forEach((agg) => {
        const weight = agg.area > 0 ? agg.area : agg.count;
        if (weight <= 0) return;
        anchors.push({
            teamId: agg.teamId,
            lat: agg.lat / weight,
            lng: agg.lng / weight,
            countyCount: agg.count,
        });
    });

    return anchors;
}

function logoSizeForCounties(count: number, maxCount: number): number {
    const min = 28;
    const max = 52;
    if (maxCount <= 0) return min;
    const t = Math.sqrt(count / maxCount);
    return Math.round(min + t * (max - min));
}

export function createTeamLogoElement(
    team: Team,
    leagueId: string,
    size: number,
    onTeamClick?: (teamId: number) => void,
): HTMLDivElement | null {
    const paths = buildTeamLogoPaths(team, leagueId);
    if (paths.length === 0) return null;

    const el = document.createElement('div');
    el.className = 'discord-team-marker';
    el.dataset.teamId = String(team.team_id);
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.title = team.team_name;
    if (onTeamClick) {
        el.style.cursor = 'pointer';
        el.style.pointerEvents = 'auto';
        el.addEventListener('click', (ev) => {
            ev.stopPropagation();
            onTeamClick(team.team_id);
        });
    }

    const img = document.createElement('img');
    img.alt = team.team_name;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'contain';
    img.style.pointerEvents = 'none';

    let attempt = 0;
    const tryNext = () => {
        if (attempt < paths.length) {
            img.setAttribute('src', paths[attempt]);
            attempt += 1;
        } else {
            el.style.display = 'none';
        }
    };
    img.onerror = tryNext;
    tryNext();
    el.appendChild(img);
    return el;
}

export function applyDiscordMarkerFocus(el: HTMLElement, teamId: number, focusedTeamId: number | null): void {
    if (focusedTeamId != null && teamId !== Number(focusedTeamId)) {
        el.classList.add('discord-team-marker--dimmed');
    } else {
        el.classList.remove('discord-team-marker--dimmed');
    }
}

export function syncDiscordTeamLogoMarkers(
    map: mapboxgl.Map,
    geojson: TerritoryGeoJSON | null,
    teams: Team[],
    leagueId: string,
    markersRef: { current: mapboxgl.Marker[] },
    options?: {
        focusedTeamId?: number | null;
        onTeamClick?: (teamId: number) => void;
        splitByState?: boolean;
    },
): void {
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (!geojson?.features.length || !teams.length) return;

    const focusedTeamId = options?.focusedTeamId ?? null;
    const teamsById = new Map(teams.map((t) => [Number(t.team_id), t]));
    const anchors = computeTeamMarkerAnchors(geojson, { splitByState: options?.splitByState ?? isDiscordEmbedded() });
    const maxCount = Math.max(...anchors.map((a) => a.countyCount), 1);

    anchors.forEach(({ teamId, lat, lng, countyCount }) => {
        const team = teamsById.get(Number(teamId));
        if (!team) return;

        const size = logoSizeForCounties(countyCount, maxCount);
        const el = createTeamLogoElement(team, leagueId, size, options?.onTeamClick);
        if (!el) return;

        applyDiscordMarkerFocus(el, Number(teamId), focusedTeamId);

        const marker = new mapboxgl.Marker({
            element: el,
            anchor: 'center',
            pitchAlignment: 'viewport',
            rotationAlignment: 'viewport',
        })
            .setLngLat([lng, lat])
            .addTo(map);
        markersRef.current.push(marker);
    });
}

function collectTeamExteriorCoords(geojson: TerritoryGeoJSON, teamId: number): [number, number][] {
    const coords: [number, number][] = [];
    geojson.features.forEach((f) => {
        if (Number(f.properties.team_id) !== teamId) return;
        const rings =
            f.geometry.type === 'Polygon'
                ? [f.geometry.coordinates[0]]
                : f.geometry.coordinates.map((p) => p[0]);
        rings.forEach((ring) => {
            (ring as number[][] | undefined)?.forEach((coord) => {
                const lng = coord[0];
                const lat = coord[1];
                if (lng == null || lat == null) return;
                coords.push([lng, lat]);
            });
        });
    });
    return coords;
}

/** Bounding box that handles Aleutian-style antimeridian crossings. */
function boundsFromCoords(coords: [number, number][]): [[number, number], [number, number]] | null {
    if (!coords.length) return null;

    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = 90;
    let maxLat = -90;

    coords.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
    });

    if (maxLng - minLng > 180) {
        minLng = Infinity;
        maxLng = -Infinity;
        coords.forEach(([lng, lat]) => {
            const adjLng = lng < 0 ? lng : lng - 360;
            minLng = Math.min(minLng, adjLng);
            maxLng = Math.max(maxLng, adjLng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
        });
    }

    return [[minLng, minLat], [maxLng, maxLat]];
}

export function boundsForTeam(
    geojson: TerritoryGeoJSON,
    teamId: number,
): [[number, number], [number, number]] | null {
    return boundsFromCoords(collectTeamExteriorCoords(geojson, teamId));
}

function collectCountyExteriorCoords(
    geojson: TerritoryGeoJSON,
    countyFips: string,
): [number, number][] {
    const coords: [number, number][] = [];
    const needle = String(countyFips);
    geojson.features.forEach((f) => {
        if (String(f.properties.county_fips) !== needle) return;
        const rings =
            f.geometry.type === 'Polygon'
                ? [f.geometry.coordinates[0]]
                : f.geometry.coordinates.map((p) => p[0]);
        rings.forEach((ring) => {
            (ring as number[][] | undefined)?.forEach((coord) => {
                const lng = coord[0];
                const lat = coord[1];
                if (lng == null || lat == null) return;
                coords.push([lng, lat]);
            });
        });
    });
    return coords;
}

export function boundsForCounty(
    geojson: TerritoryGeoJSON,
    countyFips: string,
): [[number, number], [number, number]] | null {
    return boundsFromCoords(collectCountyExteriorCoords(geojson, countyFips));
}

export function featureForCounty(
    geojson: TerritoryGeoJSON,
    countyFips: string,
): TerritoryGeoJSON['features'][number] | null {
    const needle = String(countyFips);
    return geojson.features.find((f) => String(f.properties.county_fips) === needle) ?? null;
}

export interface TeamFocusCamera {
    bounds: [[number, number], [number, number]] | null;
    center: [number, number];
    zoom?: number;
}

/** Camera target for team focus — centroid avoids dateline bbox midpoint landing in the ocean. */
export function cameraTargetForTeam(
    geojson: TerritoryGeoJSON,
    teamId: number,
    defaultZoom: number,
): TeamFocusCamera | null {
    const bounds = boundsForTeam(geojson, teamId);
    if (!bounds) return null;

    const anchor = computeTeamMarkerAnchors(geojson, { splitByState: false }).find(
        (a) => a.teamId === teamId,
    );
    const [[west, south], [east, north]] = bounds;
    const center: [number, number] = anchor
        ? [anchor.lng, anchor.lat]
        : [(west + east) / 2, (south + north) / 2];

    const spanLng = east - west;
    const spanLat = north - south;

    if (spanLng > 35 || spanLat > 22) {
        return { bounds: null, center, zoom: defaultZoom };
    }

    return { bounds, center };
}
